// Targeted partial-JSON parser for the streaming dashboard daily briefing.
// The AI emits a single ```briefing-cards {...} ``` block. While that block is
// still generating, we scan for COMPLETED top-level fields (header / summary /
// each element of cards[]) and return them so the UI can show them progressively
// instead of waiting for the final `done` event.
//
// Not a general JSON parser — only recognizes the known shape of the briefing.

function findBalancedEnd(s, startIdx, open, close) {
  let depth = 0;
  let inString = false;
  let escape = false;
  for (let i = startIdx; i < s.length; i++) {
    const c = s[i];
    if (escape) { escape = false; continue; }
    if (c === '\\' && inString) { escape = true; continue; }
    if (c === '"') { inString = !inString; continue; }
    if (inString) continue;
    if (c === open) depth++;
    else if (c === close) {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function extractStringValue(src, key) {
  const re = new RegExp(`"${key}"\\s*:\\s*"`);
  const m = src.match(re);
  if (!m) return null;
  const startIdx = m.index + m[0].length;
  let i = startIdx;
  let escape = false;
  while (i < src.length) {
    const c = src[i];
    if (escape) { escape = false; i++; continue; }
    if (c === '\\') { escape = true; i++; continue; }
    if (c === '"') {
      try { return JSON.parse(`"${src.slice(startIdx, i)}"`); } catch { return null; }
    }
    i++;
  }
  return null;
}

function extractObjectValue(src, key) {
  const re = new RegExp(`"${key}"\\s*:\\s*\\{`);
  const m = re.exec(src);
  if (!m) return null;
  const openIdx = m.index + m[0].length - 1;
  const endIdx = findBalancedEnd(src, openIdx, '{', '}');
  if (endIdx === -1) return null;
  try { return JSON.parse(src.slice(openIdx, endIdx + 1)); } catch { return null; }
}

function extractCardsArray(src) {
  const re = /"cards"\s*:\s*\[/;
  const m = re.exec(src);
  if (!m) return [];
  let i = m.index + m[0].length;
  const cards = [];
  while (i < src.length) {
    while (i < src.length && (src[i] === ',' || src[i] === ' ' || src[i] === '\n' || src[i] === '\r' || src[i] === '\t')) i++;
    if (i >= src.length) break;
    if (src[i] === ']') break;
    if (src[i] !== '{') break;
    const end = findBalancedEnd(src, i, '{', '}');
    if (end === -1) break;
    try { cards.push(JSON.parse(src.slice(i, end + 1))); } catch { /* skip malformed card */ }
    i = end + 1;
  }
  return cards;
}

// Scan `raw` for any completed parts of the briefing-cards JSON block.
// Returns { header, summary, cards, complete } — any of header/summary may be null
// if the model hasn't finished writing that field yet.
export function parsePartialBriefing(raw) {
  if (!raw) return null;
  const fenceIdx = raw.indexOf('```briefing-cards');
  if (fenceIdx === -1) return null;
  let body = raw.slice(fenceIdx + '```briefing-cards'.length);
  const closeIdx = body.indexOf('```');
  const complete = closeIdx !== -1;
  if (complete) body = body.slice(0, closeIdx);
  const braceIdx = body.indexOf('{');
  if (braceIdx === -1) return null;
  const src = body.slice(braceIdx);

  return {
    header: extractObjectValue(src, 'header'),
    summary: extractStringValue(src, 'summary'),
    cards: extractCardsArray(src),
    complete,
  };
}
